import { EntityListTypeSupported, getEntityListSpecification } from '../entityListsLib';
import { EntityListResponse, EntityListTypeName, ValidationProblemCode } from '@ydistri/api-sdk';
import { useNavigate } from 'react-router';
import React, { useCallback, useMemo, useState } from 'react';
import {
  EntityListUpdateRequest,
  useCreateEntityListMutation,
  useGetEntityListsQuery,
  useUpdateEntityListMutation,
} from '../apiEntityLists';
import { ModalMode } from '../../common/administrationItemsTypes';
import { useAppDispatch } from '../../../../store';
import { ButtonProps, Checkbox, CheckboxOptionType, Form, Input as AntDInput, Modal } from 'antd';
import dayjs from 'dayjs';
import { RoutingPage } from '../../../../routes/routerSlice';
import { addToast } from '@ydistri/utils';
import {
  fieldErrors,
  messageFor,
  problemMessagesOf,
  problemsOfError,
} from '../../../Configuration/Validator/problemsLib';
import { setSelectedEntityList } from '../entityListAdministrationSlice';
import { Column, FormattedDatePicker, Input } from '@ydistri/ds';
import { Rule } from 'rc-field-form/lib/interface';
import {
  DateTimeFormats,
  DESCRIPTION_ITEM_MAX_LENGTH,
  RangeConstants,
} from '../../../../components/global/_constants';
import { useStableParams } from '../../../../routes/useStableParams';

const { TextArea } = AntDInput;
const descriptionFieldParams = { minRows: 4, maxRows: 6 };

interface EntityListModalProps {
  entityListType: EntityListTypeSupported;
  onClose: () => void;
  entityList?: EntityListResponse;
  confirmButtonLabel?: string;
}

interface FormType {
  name: string;
  scheduledArchiveOn: dayjs.Dayjs | null;
  description?: string;
  entityListTypes?: EntityListTypeName[];
}

const formLayout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 21 },
};

/** The server's paths on the form's fields. */
const fieldOfPath: Record<string, keyof FormType> = {
  name: 'name',
  entityListTypeNames: 'entityListTypes',
  scheduledArchiveOn: 'scheduledArchiveOn',
};

const EntityListModal: React.FC<EntityListModalProps> = ({
  entityListType,
  onClose,
  entityList,
  confirmButtonLabel,
}) => {
  const { projectShortName } = useStableParams();
  const navigate = useNavigate();
  const [isBusy, setIsBusy] = useState(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [addEntityList] = useCreateEntityListMutation();
  const [updateEntityList] = useUpdateEntityListMutation();
  const entityListDef = getEntityListSpecification(entityListType);
  const [form] = Form.useForm<FormType>();

  const { data: entityLists, isFetching } = useGetEntityListsQuery({});

  //cannot use any name of a same listItemType list (Sku, SkuQuantity)
  const restrictedNames = useMemo((): Set<string> => {
    if (!isFetching && entityLists && entityLists.length > 0) {
      const listItemType = getEntityListSpecification(entityListType).itemType;

      const currentEntityListId = entityList?.entityListId;
      const currentEntityListName = (entityList?.name ?? '').toLocaleLowerCase();
      return new Set(
        entityLists
          .filter(entityList =>
            entityList.entityListTypes.some(item => item.entityListItemType === listItemType),
          )
          // The backend's name check ignores lists whose data were deleted, so their names are free.
          .filter(entityList => !entityList.isDataDeleted)
          .filter(listItem => {
            if (currentEntityListId && listItem.entityListId === currentEntityListId) {
              return false;
            }

            if (!currentEntityListId && currentEntityListName.length > 0) {
              return (listItem.name ?? '').toLocaleLowerCase() !== currentEntityListName;
            }

            return true;
          })
          .map(entityList => (entityList.name ?? '').toLocaleLowerCase())
          .filter(name => name.length > 0),
      );
    } else {
      return new Set();
    }
  }, [entityList?.entityListId, entityList?.name, entityListType, entityLists, isFetching]);

  // checks if the new name is already used
  const titleInputRules = useMemo(
    () => [
      { required: true, message: messageFor(ValidationProblemCode.EntityListNameRequired) },
      {
        validator: (rule: Rule, value: string) => {
          return new Promise((resolve, reject) => {
            if (!value) {
              resolve(value);
              return;
            }
            //server validation is case insensitive, so we need to convert the value to lower case before checking
            const valueLowerCase = value.toLocaleLowerCase();
            if (restrictedNames.has(valueLowerCase)) {
              reject(new Error(messageFor(ValidationProblemCode.EntityListNameTaken)));
            } else {
              resolve(value);
            }
          });
        },
      },
    ],
    [restrictedNames],
  );

  const modalMode = entityList !== undefined ? ModalMode.EDIT : ModalMode.CREATE;

  const initialValues = useMemo((): FormType => {
    if (entityList) {
      return {
        name: entityList.name ?? '',
        scheduledArchiveOn: entityList.scheduledArchiveOn
          ? dayjs(entityList.scheduledArchiveOn)
          : null,
        description: entityList.description ?? '',
        entityListTypes: entityList.entityListTypes.map(type => type.entityListTypeName),
      };
    } else {
      return {
        name: '',
        entityListTypes: [entityListType],
        scheduledArchiveOn: dayjs().add(
          RangeConstants.DEFAULT_ENTITY_LIST_SCHEDULED_ARCHIVE_PERIOD,
          'day',
        ),
      };
    }
  }, [entityList, entityListType]);

  const dispatch = useAppDispatch();

  const closeModal = useCallback(() => {
    onClose();
  }, [onClose]); //setIsOpened

  /**
   * A refused save: the problems the form has a field for go under it and keep the modal open, the
   * rest goes to a toast. Returns whether the modal stays open.
   */
  const showRefusal = useCallback(
    (error: unknown, fallback: string): boolean => {
      const { fields, other } = fieldErrors(problemsOfError(error), fieldOfPath);
      if (fields.length > 0) {
        form.setFields(fields);
        setIsError(true);
      }
      if (fields.length === 0 || other.length > 0) {
        const detail = other.length > 0 ? other.join(' ') : problemMessagesOf(error, '');
        dispatch(
          addToast({ message: detail === '' ? fallback : `${fallback}: ${detail}`, isError: true }),
        );
      }
      return fields.length > 0;
    },
    [dispatch, form],
  );

  /**
   * Clicks were passed through to an underlying element, so we need to stop propagation
   */
  const closeHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  const createEntityList = useCallback(
    (values: FormType) => {
      setIsBusy(true);

      let effectiveEntityListTypes: EntityListTypeName[] = [entityListType];
      if (values.entityListTypes && values.entityListTypes.length > 0) {
        effectiveEntityListTypes = values.entityListTypes;
      }

      let stayOpen = false;
      addEntityList({
        entityListTypeNames: effectiveEntityListTypes,
        name: values.name,
        scheduledArchiveOn: values.scheduledArchiveOn?.format(DateTimeFormats.API_DATE) ?? null,
        description: values.description,
      })
        .unwrap()
        .then(result => {
          setIsError(false);
          navigate(
            `/${projectShortName}/${RoutingPage.PROJECT_ADMINISTRATION}/${entityListDef.subpage}/${result.entityListId}`,
          );
        })
        .catch((error: unknown) => {
          stayOpen = showRefusal(error, 'List was not created');
        })
        .finally(() => {
          setIsBusy(false);
          if (!stayOpen) closeModal();
        });
    },
    [
      addEntityList,
      entityListType,
      navigate,
      projectShortName,
      entityListDef.subpage,
      showRefusal,
      closeModal,
    ],
  );

  const editEntityList = useCallback(
    (values: FormType) => {
      if (entityList) {
        setIsBusy(true);

        const request: EntityListUpdateRequest = {
          entityListId: entityList.entityListId,
          entityListTypeNames: values.entityListTypes,
          name: values.name,
          scheduledArchiveOn: values.scheduledArchiveOn?.format(DateTimeFormats.API_DATE) ?? null,
          description: values.description,
        };

        let stayOpen = false;
        updateEntityList(request)
          .unwrap()
          .then(result => {
            dispatch(setSelectedEntityList({ entityListType, data: result }));
          })
          .catch((error: unknown) => {
            stayOpen = showRefusal(error, `${entityListDef.title} was not updated`);
          })
          .finally(() => {
            setIsBusy(false);
            if (!stayOpen) closeModal();
          });
      }
    },
    [
      closeModal,
      dispatch,
      entityList,
      entityListDef.title,
      entityListType,
      showRefusal,
      updateEntityList,
    ],
  );

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then(values => {
        if (modalMode === ModalMode.CREATE) {
          createEntityList(values);
        } else {
          editEntityList(values);
        }
      })
      .catch(() => {
        dispatch(
          addToast({
            message: 'Please check required fields',
            isError: true,
          }),
        );
      });
  }, [createEntityList, dispatch, editEntityList, form, modalMode]);

  const okButtonProps: ButtonProps = useMemo(() => {
    const isDisabled = isBusy || isError || isFetching;

    return {
      disabled: isDisabled,
      loading: isBusy,
    };
  }, [isError, isBusy, isFetching]);

  /**
   * Clicks were passed through to an underlying element, so we need to stop propagation
   */
  const okHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handleSubmit();
    },
    [handleSubmit],
  );

  /**
   * We need to check for validation errors on every field change, so we can disable
   * the OK/Submit button
   */
  const onFormFieldsChange = useCallback(() => {
    const hasValidationErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
    setIsError(hasValidationErrors);
  }, [form]);

  const relatedEntityListTypeOptions = useMemo(() => {
    const relatedTypes: CheckboxOptionType<string>[] | undefined =
      entityListDef.relatedEntityListTypes.map(type => ({ label: type, value: type }));

    if (relatedTypes.length > 0) {
      const defaultType: CheckboxOptionType<string> = {
        label: entityListType,
        value: entityListType,
        disabled: modalMode === ModalMode.CREATE,
      };

      return [defaultType, ...relatedTypes].sort((a, b) => a.value.localeCompare(b.value));
    } else {
      return undefined;
    }
  }, [entityListDef.relatedEntityListTypes, entityListType, modalMode]);

  //required only when there are multiple options and checkbox is rendered
  const typeInputRules = useMemo(() => {
    return [
      {
        required: !!relatedEntityListTypeOptions,
        message: messageFor(ValidationProblemCode.EntityListTypeRequired),
      },
    ];
  }, [relatedEntityListTypeOptions]);

  return (
    <Modal
      data-type="modal"
      title={
        modalMode === ModalMode.CREATE
          ? `Create ${entityListDef.title}`
          : `Edit ${entityListDef.title} "${entityList?.name ?? ''}"`
      }
      open={true}
      okText={confirmButtonLabel ?? 'OK'}
      onOk={okHandler}
      onCancel={closeHandler}
      maskClosable={false}
      okButtonProps={okButtonProps}
      closable={false}
      width={640}
    >
      <Column $padding={1}>
        <Form
          data-type="entitylist-form"
          form={form}
          labelCol={formLayout.labelCol}
          wrapperCol={formLayout.wrapperCol}
          name="entitylist_modal"
          layout="horizontal"
          initialValues={initialValues}
          autoComplete="off"
          onFieldsChange={onFormFieldsChange}
        >
          <Form.Item<FormType> name="name" label="Title" rules={titleInputRules}>
            <Input onPressEnter={handleSubmit} autoFocus />
          </Form.Item>
          {relatedEntityListTypeOptions && (
            <Form.Item<FormType> name="entityListTypes" label="Use as" rules={typeInputRules}>
              <Checkbox.Group options={relatedEntityListTypeOptions} />
            </Form.Item>
          )}
          <Form.Item<FormType> name="scheduledArchiveOn" label="Archive On">
            <FormattedDatePicker />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <TextArea
              name="description"
              placeholder="Enter description for the entity list"
              autoSize={descriptionFieldParams}
              maxLength={DESCRIPTION_ITEM_MAX_LENGTH}
            />
          </Form.Item>
        </Form>
      </Column>
    </Modal>
  );
};

export default EntityListModal;
